import { datadogRum } from '@datadog/browser-rum';
import React, { FC, useEffect } from 'react';
import styled from 'styled-components';

import { isPROD } from '@webtrader/core/configs';
import { useCompanySettings } from '@webtrader/core/hooks/company';
import { useAuthWithKeys } from '@webtrader/core/services/auth';
import { useTheme } from '@webtrader/core/services/theme/v2';

import Spinner from '@/components/Loaders/Spin';
import { usePortalContext } from '@/components/Portal/PortalContext';
import { setPortalContext } from '@/components/Portal/PortalUtil';

import AuthenticationNavigation from './web/Authentication';
import MainNavigation from './web/Main';

interface IProps {}

const RootNavigation: FC<IProps> = () => {
  const { authenticated, ready, selectedTradingAccount, splashLoading } = useAuthWithKeys([
    'authenticated',
    'ready',
    'selectedTradingAccount',
    'splashLoading'
  ]);
  const { theme } = useTheme();
  const { company, loading } = useCompanySettings();

  const { teleport, removeGate, removeAllGate } = usePortalContext();

  useEffect(() => {
    setPortalContext({
      gates: {},
      teleport,
      removeGate,
      removeAllGate
    });
  }, [removeAllGate, removeGate, teleport]);

  // This Datadog function will only run on PROD
  useEffect(() => {
    if (isPROD()) {
      datadogRum.setUser({
        id: selectedTradingAccount?.accountNumber.toString(),
        tenantId: company?.tenantId
      });
    }
  }, [company?.tenantId, selectedTradingAccount?.accountNumber]);

  if (!ready || splashLoading || loading) {
    return (
      <Container backgroundColor={theme.colors.background[0]}>
        <Spinner size={36} color={theme.colors.primary} />
      </Container>
    );
  }

  if (!authenticated) {
    return <AuthenticationNavigation />;
  }

  return <MainNavigation />;
};

export default RootNavigation;

const Container = styled.div<{ backgroundColor: string }>`
  display: flex;
  align-items: center;
  justify-content: center;

  height: 100%;
  width: 100%;
  background-color: ${({ backgroundColor }) => backgroundColor};
`;
